import { IconPhotoFilled } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FocusContainer, OverlayLink } from "src/components/UI/OverlayLink/OverlayLink";
import PaddedView from "src/components/UI/PaddedView/PaddedView";
import { useDeviceContext } from "src/context/device-context";
import { useLocaleContext } from "src/context/locale-context";
import classes from "./CheapDestinations.module.css";
import { useImageIntersectionObserver } from "src/hooks/useImageIntersectionObserver";

function CheapDestination({ item }) {
  const [imgErr, setImgErr] = useState(false);
  const { getStringRes, currencySymbol } = useLocaleContext();

  return (
    <FocusContainer tag="div" className={classes.item}>
      {imgErr ? (
        <div className={classes.imgError}>
          <div className={classes.frame}>
            <IconPhotoFilled size="100%" color="var(--ui-4)" />
          </div>
        </div>
      ) : (
        <img
          onError={() => setImgErr(true)}
          alt={item.city}
          data-src={
            `https://${window.SERVER_DATA.REACT_APP_BUCKET_NAME}.` +
            `${window.SERVER_DATA.REACT_APP_BUCKET_PATH}/cities/` +
            `${item.iata}.jpg`
          }
        />
      )}
      <OverlayLink
        className={classes.link}
        data-focus-trigger
        to={item.link.startsWith("/") ? item.link : `/${item.link}`}>
        <h3>{item.city}</h3>
        <p>
          <span>{getStringRes("cheap.flights.price.from")}</span>
          {` ${item.price} ${currencySymbol}`}
        </p>
      </OverlayLink>
    </FocusContainer>
  );
}

function CheapDestinationGrid({ items }) {
  const containerRef = useRef(null);
  useImageIntersectionObserver(containerRef);

  return (
    <motion.ul
      ref={containerRef}
      initial="hidden"
      animate="visible"
      variants={listContainerVariants}
      className={classes.tripsList}>
      {items.map((item) => (
        <motion.li
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          variants={listItemVariants}
          initial="hidden"
          animate="visible"
          tabIndex={-1}
          key={item.id}>
          <CheapDestination item={item} />
        </motion.li>
      ))}
    </motion.ul>
  );
}

const listContainerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };

const listItemVariants = {
  hidden: { opacity: 0, scale: 0.9, y: -10 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.33, ease: "backOut" },
  },
};

const STR_VARS = { company: "##COMPANY_NAME##" };

export function CheapDestinations({ data }) {
  const { isMobile } = useDeviceContext();
  const { getStringRes } = useLocaleContext();

  if (data?.length === 0) {
    return null;
  }

  return (
    <section
      className={`${classes.cheapDestinations} ${
        isMobile ? classes.mobile : classes.desktop
      }`}>
      <PaddedView>
        <div className={classes.heading}>
          <h2>
            {getStringRes("cheap.travel.deals.other.title").replace(
              STR_VARS.company,
              window.SERVER_DATA.REACT_APP_PROJ_NAME
            )}
          </h2>
          <Link className={classes.more} to="/cheap-flights">
            {getStringRes("cheap.travel.deals.button.more")}
          </Link>
        </div>
        <p className={classes.desc}>{getStringRes("cheap.travel.deals.other.description")}</p>
        <CheapDestinationGrid items={data} />
        <Link className={classes.more} to="/cheap-flights">
          {getStringRes("cheap.travel.deals.button.more")}
        </Link>
      </PaddedView>
    </section>
  );
}
